import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";

const slugify = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/(^-|-$)/g, "").toLowerCase();

function Col({ title, links, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="earmark mb-5 text-black">{title}</div>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to}><Link to={l.to} data-testid={`${testid}-link-${l.to.split("/").filter(Boolean).join("-")}`} className="break-words text-[13.5px] text-[var(--ink-2)] hover:text-black transition-colors duration-300">{l.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const { content, lang, setLang, loading } = useLang();
  const { ui, footer } = content;
  const services = content.services.map((s) => ({ to: `/services/${s.slug}`, label: s.name }));
  const activities = content.departments.map((d) => ({ to: `/activites/${d.slug}`, label: d.name }));
  const sectors = content.sectors.map((s) => ({ to: `/solutions/${s.slug}`, label: s.name }));
  const techs = content.technologies.map((t) => ({ to: `/technologies/${t.slug}`, label: t.name }));
  const company = footer.company.map((o) => ({ to: `/legal/${slugify(o)}`, label: o }));
  const others = footer.other.map((o, i) => ({ to: i === 0 ? "/carrieres" : "/contact", label: o }));

  return (
    <footer data-testid="site-footer" className="relative border-t border-[var(--line)] bg-[var(--bg-grey)]">
      <div className="container-x pt-20 pb-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <Logo className="h-12" testId="footer-official-logo" />
            <p data-testid="footer-tagline" className="mt-4 font-head text-base md:text-lg tracking-tight text-black">Tomorrow is Now</p>
          </div>
          <div className="lg:col-span-9 grid grid-cols-2 gap-10 md:grid-cols-4">
            <div className="min-w-0 space-y-10">
              <Col title={ui.services} links={services} testid="footer-col-services" />
              <Col title={ui.activities} links={activities} testid="footer-col-activities" />
            </div>
            <Col title={ui.solutions} links={sectors} testid="footer-col-solutions" />
            <Col title={ui.technologies} links={techs} testid="footer-col-technologies" />
            <div className="min-w-0 space-y-10">
              <Col title={ui.company} links={company} testid="footer-col-company" />
              <Col title={ui.other} links={others} testid="footer-col-other" />
            </div>
          </div>
        </div>
        <div className="rule mt-16 mb-6" />
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4" data-testid="language-switcher">
            <span className="earmark">{ui.language}</span>
            <div className="inline-flex border border-black/20">
              {["fr", "en"].map((l) => (
                <button key={l} data-testid={`lang-toggle-${l}`} aria-pressed={lang === l} onClick={() => setLang(l)}
                  className={`px-4 py-2 text-[12px] font-medium tracking-wide transition-colors duration-300 ${lang === l ? `bg-black text-white ${loading ? "animate-pulse" : ""}` : "text-[var(--ink-2)] hover:text-black"}`}>
                  {l === "fr" ? "Français" : "English"}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[12px] text-[var(--ink-3)]" data-testid="footer-copyright">{footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
